import reactFrontendRoutes from 'src/router/reactFrontendRoutes'
import { LEASING_ENABLED } from 'src/config/releaseFeatures'
import { punchRoutes } from './punch_routes'

import SystemSettings from 'src/composites/Admin/SystemSettings/SystemSettings.vue'
import PartnerDedupPage from 'src/composites/PartnerDedup/PartnerDedupPage.vue'
import { partsDashboardRoutes } from 'src/composites/PartsDashboard/routes'
import { clientProfileRoutes } from 'src/composites/ClientProfile/routes'
import { complianceRegisterRoutes } from 'src/composites/ComplianceRegisters/routes'
import { creditNoteRoutes } from 'src/composites/CreditNotes/routes'
import { financesRoutes } from 'src/composites/Finances/routes'
import StockTransferPage from 'src/composites/StockManager/components/StockTransferPage.vue'
import { paymentRoutes } from 'src/composites/TransactionPayment/routes'
import { transactionDashboardRoutes } from 'src/composites/TransactionProcessingDashboard'
import crudRoutes from 'src/CrudManager/Core/router/crud_routes'
import { useUserStore } from 'src/CrudManager/Core/stores/useUserStore'
import MainLayout from 'src/layouts/MainLayout.vue'
import { type RouteRecordRaw } from 'vue-router'
import authRoutes from './auth_routes'

const requireAuthenticatedUser = () => {
  const userStore = useUserStore()
  return userStore.loggedIn && userStore.token ? true : '/login'
}

const routes: RouteRecordRaw[] = [
  ...authRoutes,
  {
    path: '/react',
    beforeEnter: requireAuthenticatedUser,
    children: reactFrontendRoutes,
  },
  {
    path: '/',
    component: MainLayout,
    beforeEnter: requireAuthenticatedUser,
    children: [
      { path: '/', component: () => import('src/pages/IndexPage.vue'), name: 'home' },
      { path: '/profile', component: () => import('src/pages/ProfilePage.vue'), name: 'profile' },
      { path: '/settings', component: () => import('src/pages/SettingsPage.vue'), name: 'settings' },
      { path: '/crudmanager', component: () => import('src/CrudManager/Core/composites/Layout/CrudManager.vue') },
      { path: 'policy', component: () => import('src/pages/PolicyPage.vue') },
      {
        path: '/dashboard/reports',
        component: () => import('src/pages/Reports/ReportsPage.vue'),
        name: 'reports',
        beforeEnter: () => {
          const userStore = useUserStore()
          if (!userStore.hasPermission('see.reports.page')) {
            return { path: '/' }
          }
          return true
        },
      },
      {
        path: '/dashboard/reports/tag-commission',
        component: () => import('src/pages/Reports/TagCommissionReport.vue'),
        name: 'tag-commission-report',
        beforeEnter: () => {
          const userStore = useUserStore()
          if (!userStore.hasPermission('see.reports.financial')) {
            return { path: '/' }
          }
          return true
        },
      },
      { path: '/dashboard/stock', component: () => import('src/pages/Reports/AnnualStockReport.vue'), name: 'dashboard' },
      { path: '/dashboard/sales', component: () => import('src/pages/Reports/SalesReport/MonthlySalesReport.vue'), name: 'sales' },
      {
        path: '/leasing/overview',
        name: 'leasing-overview',
        component: () => import('src/composites/LeasingOverview/LeasingOverviewPage.vue'),
        meta: {
          title: 'Leasing Overview',
          requiresAuth: true,
        },
        beforeEnter: () => {
          if (!LEASING_ENABLED) return { path: '/' }
          const userStore = useUserStore()
          if (!userStore.hasPermission('transactions.review')) {
            return { path: '/' }
          }
          return true
        },
      },
      {
        path: '/stock',
        children: [
          {
            path: 'transfer',
            name: 'stock-transfer',
            component: StockTransferPage,
            meta: {
              title: 'Stock Transfer',
              requiresAuth: true,
            },
          },
        ],
      },
      {
        path: '/admin',
        children: [
          {
            path: 'system-settings',
            name: 'system-settings',
            component: SystemSettings,
            meta: {
              title: 'System Settings',
              requiresAuth: true,
            },
          },
          {
            path: 'business-partners/dedup',
            name: 'partner-dedup',
            component: PartnerDedupPage,
            meta: {
              title: 'Partner Deduplication',
              requiresAuth: true,
            },
            beforeEnter: () => {
              const userStore = useUserStore()
              if (!userStore.hasPermission('manage.business-partners.merge')) {
                return { path: '/' }
              }
              return true
            },
          },
        ],
      },
      ...clientProfileRoutes,
      ...crudRoutes,
      ...punchRoutes,
      ...transactionDashboardRoutes,
      ...paymentRoutes,
      ...creditNoteRoutes,
      ...complianceRegisterRoutes,
      ...financesRoutes,
      ...partsDashboardRoutes,
    ],
  },

  /*
   * Always leave this as last one,
   * but you can also remove it
   */
  {
    path: '/:catchAll(.*)*',
    component: () => import('src/pages/ErrorNotFound.vue'),
  },
]

export default routes
